import { useSessionContext } from "@supabase/auth-helpers-react";
import { useCallback, useEffect, createContext, useContext, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useAppSelector } from "@/common/hooks/useRedux";
import { isRecorderActive, selectRecorderActive } from "@common/lib/recorderActivity";
import { getAppVersion } from "@/common/lib/supabaseClient";
import * as Sentry from "@sentry/react";
import ConfirmModal from "@/common/components/ConfirmModal";

const CacheBusterContext = createContext({ refreshApp: () => {} });

export const useCacheBuster = () => useContext(CacheBusterContext);

function devLogCacheBuster(message: string, details?: Record<string, unknown>) {
    if (import.meta.env.DEV) {
        if (details) {
            console.debug("[CacheBuster]", message, details);
        } else {
            console.debug("[CacheBuster]", message);
        }
    }
}

/** sessionStorage: number of reloads already triggered after app_versions fetch failure (capped at 3). */
const APP_VERSION_FAIL_RELOAD_COUNT_KEY = "appVersionFetchFailReloadCount";
const MAX_APP_VERSION_FAIL_RELOADS = 3;
/** Backoff before each failure-driven reload: 1st immediate, 2nd after 2s, 3rd after 8s. */
const APP_VERSION_FAIL_RELOAD_BACKOFF_MS = [0, 2_000, 8_000] as const;
/** URL parameter to track reload count when sessionStorage is unavailable. */
const URL_RELOAD_COUNT_PARAM = "appVersionReloadCount";

/** Resets to false on every page refresh because module code re-executes on each load. */
let firstVersionCheckRan = false;
export const _setFirstVersionCheckRanForTesting = (value: boolean) => {
    firstVersionCheckRan = value;
};

/** sessionStorage: epoch ms until app version checks resume after user taps "Later". */
const APP_VERSION_CHECK_SNOOZE_UNTIL_KEY = "appVersionCheckSnoozeUntil";
const VERSION_CHECK_SNOOZE_MS = 2 * 60 * 60 * 1000;

function readVersionCheckSnoozeUntil(): number {
    try {
        const raw = sessionStorage.getItem(APP_VERSION_CHECK_SNOOZE_UNTIL_KEY);
        if (!raw) return 0;
        const n = Number(raw);
        return Number.isFinite(n) ? n : 0;
    } catch {
        return 0;
    }
}

function isVersionCheckSnoozed(): boolean {
    return Date.now() < readVersionCheckSnoozeUntil();
}

function snoozeVersionChecksForTwoHours(): void {
    try {
        sessionStorage.setItem(APP_VERSION_CHECK_SNOOZE_UNTIL_KEY, String(Date.now() + VERSION_CHECK_SNOOZE_MS));
    } catch {
        /* ignore */
    }
}

/** localStorage: epoch ms when the version refresh modal was last shown — at most once per week. */
const APP_VERSION_REFRESH_MODAL_LAST_SHOWN_AT_KEY = "appVersionRefreshModalLastShownAt";
const LEGACY_APP_VERSION_REFRESH_MODAL_LAST_SHOWN_DAY_KEY = "appVersionRefreshModalLastShownDay";
const VERSION_REFRESH_MODAL_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

function readLegacyVersionRefreshModalLastShownAt(): number {
    const raw = localStorage.getItem(LEGACY_APP_VERSION_REFRESH_MODAL_LAST_SHOWN_DAY_KEY);
    if (!raw) return 0;

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    if (!match) return 0;

    const [, year, month, day] = match;
    const legacyDate = new Date(Number(year), Number(month) - 1, Number(day));
    const legacyTime = legacyDate.getTime();
    return Number.isFinite(legacyTime) ? legacyTime : 0;
}

function wasVersionRefreshModalShownThisWeek(): boolean {
    try {
        const raw = localStorage.getItem(APP_VERSION_REFRESH_MODAL_LAST_SHOWN_AT_KEY);
        const lastShownAt = Number(raw);
        const shownAt =
            raw !== null && Number.isFinite(lastShownAt) ? lastShownAt : readLegacyVersionRefreshModalLastShownAt();
        return shownAt > 0 && Date.now() - shownAt < VERSION_REFRESH_MODAL_INTERVAL_MS;
    } catch {
        return false;
    }
}

function markVersionRefreshModalShownThisWeek(): void {
    try {
        localStorage.setItem(APP_VERSION_REFRESH_MODAL_LAST_SHOWN_AT_KEY, String(Date.now()));
        localStorage.removeItem(LEGACY_APP_VERSION_REFRESH_MODAL_LAST_SHOWN_DAY_KEY);
    } catch {
        /* ignore */
    }
}

/**
 * Fresh recording/upload/completion flags from the store (also effect deps re-run the check
 * when these change). Delegates to the shared predicate (EN-837) so the "recorder busy?" rule
 * lives in one place; this additionally defers a reload while a document is completing.
 */
function isUnsafeToReload(): boolean {
    return isRecorderActive();
}

const CacheBuster = ({ children }: { children: React.ReactNode }) => {
    const { session } = useSessionContext();
    const { globalRecordingState, isUploadingGlobal, completing, finalizing } = useAppSelector(
        (state) => state.globalState,
    );
    const location = useLocation();
    const failureReloadTimeoutRef = useRef<number | null>(null);
    const pendingRefreshVersionRef = useRef<{ id: number; version: number } | null>(null);
    const [showRefreshModal, setShowRefreshModal] = useState(false);
    // EN-860: set when the user taps Refresh while the recorder is still busy — keeps the modal up
    // with a "still recording" hint instead of silently deferring a hard reload. Cleared once idle.
    const [refreshBlockedByRecording, setRefreshBlockedByRecording] = useState(false);
    const recorderBusy = selectRecorderActive({ globalRecordingState, isUploadingGlobal, completing, finalizing });

    const refreshApp = useCallback(() => {
        if (caches) {
            caches.keys().then((names) => {
                for (const name of names) caches.delete(name);
            });
        }

        window.location.reload();
    }, []);

    // Drop the "still recording" refresh hint as soon as the recorder goes idle, so it can never
    // linger stale after the recording/upload that blocked the reload has finished.
    useEffect(() => {
        if (!recorderBusy) setRefreshBlockedByRecording(false);
    }, [recorderBusy]);

    useEffect(() => {
        const abortController = new AbortController();

        if (!session?.user) {
            return () => {
                abortController.abort();
                if (failureReloadTimeoutRef.current !== null) {
                    clearTimeout(failureReloadTimeoutRef.current);
                    failureReloadTimeoutRef.current = null;
                }
            };
        }

        const checkVersionUpdate = async (signal: AbortSignal): Promise<void> => {
            const isFreshPageLoad = !firstVersionCheckRan;
            firstVersionCheckRan = true;

            try {
                if (!isFreshPageLoad && isVersionCheckSnoozed()) {
                    devLogCacheBuster("version check skipped (snoozed)", {
                        snoozeUntilIso: new Date(readVersionCheckSnoozeUntil()).toISOString(),
                        pathname: location.pathname,
                    });
                    return;
                }

                devLogCacheBuster("version check started", { pathname: location.pathname });

                const version = await getAppVersion(signal);

                if (signal.aborted) return;

                if (!version) {
                    let failReloadCount = 0;
                    try {
                        failReloadCount = Number(sessionStorage.getItem(APP_VERSION_FAIL_RELOAD_COUNT_KEY) || "0");
                    } catch {
                        const urlParams = new URLSearchParams(window.location.search);
                        failReloadCount = Number(urlParams.get(URL_RELOAD_COUNT_PARAM) || "0");
                    }
                    if (failReloadCount >= MAX_APP_VERSION_FAIL_RELOADS) {
                        devLogCacheBuster(
                            "app_versions fetch returned null — max failure reloads reached, not retrying",
                            {
                                pathname: location.pathname,
                            },
                        );
                        return;
                    }

                    if (!isUnsafeToReload()) {
                        if (signal.aborted) return;
                        const delay =
                            APP_VERSION_FAIL_RELOAD_BACKOFF_MS[
                                Math.min(failReloadCount, APP_VERSION_FAIL_RELOAD_BACKOFF_MS.length - 1)
                            ];
                        devLogCacheBuster("app_versions fetch returned null — scheduling reload backoff", {
                            failReloadCount,
                            delayMs: delay,
                            pathname: location.pathname,
                        });
                        failureReloadTimeoutRef.current = window.setTimeout(() => {
                            failureReloadTimeoutRef.current = null;
                            if (signal.aborted) return;
                            if (isUnsafeToReload()) return;
                            const nextCount = failReloadCount + 1;
                            try {
                                sessionStorage.setItem(APP_VERSION_FAIL_RELOAD_COUNT_KEY, String(nextCount));
                            } catch {
                                const url = new URL(window.location.href);
                                url.searchParams.set(URL_RELOAD_COUNT_PARAM, String(nextCount));
                                window.location.href = url.toString();
                                return;
                            }
                            refreshApp();
                        }, delay);
                    } else {
                        devLogCacheBuster(
                            "app_versions fetch returned null — reload backoff deferred (recording or upload)",
                            {
                                failReloadCount,
                                pathname: location.pathname,
                            },
                        );
                    }
                    return;
                }

                try {
                    sessionStorage.removeItem(APP_VERSION_FAIL_RELOAD_COUNT_KEY);
                } catch {
                    /* ignore */
                }
                if (window.location.search.includes(URL_RELOAD_COUNT_PARAM)) {
                    const url = new URL(window.location.href);
                    url.searchParams.delete(URL_RELOAD_COUNT_PARAM);
                    window.history.replaceState({}, "", url.toString());
                }

                const latestVersion = version.version;
                const localVersion = localStorage.getItem("app_versions");
                const currentVersion = localVersion ? JSON.parse(localVersion).version : 0;

                if (!localVersion) {
                    localStorage.setItem("app_versions", JSON.stringify({ id: version.id, version: latestVersion }));
                    Sentry.setTag("app_version", latestVersion.toString());
                    devLogCacheBuster("version check complete — seeded local app_versions", {
                        remoteVersion: latestVersion,
                        pathname: location.pathname,
                    });
                    return;
                }

                if (latestVersion.toString() !== currentVersion.toString()) {
                    if (signal.aborted) return;

                    if (isFreshPageLoad) {
                        // The browser already loaded new bundles on this refresh — update localStorage silently.
                        localStorage.setItem(
                            "app_versions",
                            JSON.stringify({ id: version.id, version: latestVersion }),
                        );
                        Sentry.setTag("app_version", latestVersion.toString());
                        devLogCacheBuster("version check complete — updated local app_versions on fresh page load", {
                            localVersion: currentVersion,
                            remoteVersion: latestVersion,
                            pathname: location.pathname,
                        });
                        return;
                    }

                    if (isUnsafeToReload()) {
                        devLogCacheBuster("update available but deferred (recording or upload)", {
                            localVersion: currentVersion,
                            remoteVersion: latestVersion,
                            pathname: location.pathname,
                        });
                        return;
                    }

                    if (wasVersionRefreshModalShownThisWeek()) {
                        devLogCacheBuster("update available — refresh modal suppressed (already shown this week)", {
                            localVersion: currentVersion,
                            remoteVersion: latestVersion,
                            pathname: location.pathname,
                        });
                        return;
                    }

                    pendingRefreshVersionRef.current = { id: version.id, version: latestVersion };
                    markVersionRefreshModalShownThisWeek();
                    setShowRefreshModal(true);
                    devLogCacheBuster("update available — showing refresh modal", {
                        localVersion: currentVersion,
                        remoteVersion: latestVersion,
                        pathname: location.pathname,
                    });
                    return;
                }

                devLogCacheBuster("version check complete — up to date", {
                    version: latestVersion,
                    pathname: location.pathname,
                });
            } finally {
                // React Strict Mode mounts → unmounts → remounts every component in development,
                // aborting the first effect run's signal before it completes. Without this reset,
                // firstVersionCheckRan stays true after the aborted run, so the second (real) run
                // sees isFreshPageLoad=false and falls through to the modal path. Same issue occurs
                // if a navigation fires during the Supabase fetch. Reset so the next run still gets
                // a fresh-page-load treatment.
                if (isFreshPageLoad && signal.aborted) {
                    firstVersionCheckRan = false;
                }
            }
        };

        void checkVersionUpdate(abortController.signal);

        return () => {
            abortController.abort();
            if (failureReloadTimeoutRef.current !== null) {
                clearTimeout(failureReloadTimeoutRef.current);
                failureReloadTimeoutRef.current = null;
            }
        };
        // Re-run when recording/upload/completion finish so we can prompt refresh after an in-flight
        // visit ends. `completing` is included because isUnsafeToReload() (→ isRecorderActive) treats
        // it as busy: without it, a check deferred during a completing-only window would never
        // re-evaluate when completing clears, silently skipping the refresh prompt.
    }, [
        location.pathname,
        session?.user?.id,
        globalRecordingState,
        isUploadingGlobal,
        completing,
        finalizing,
        refreshApp,
    ]);

    const handleConfirmRefresh = () => {
        // EN-837/EN-860: the display guard only checks at modal-show time. If the recorder went busy
        // *after* the modal appeared (e.g. the upload-flag window), reloading now would tear down the
        // clip mid-flight. Don't reload — and don't silently queue a delayed hard reload either. Keep
        // the modal up, tell the vet why, and let them tap Refresh again once idle (mirrors
        // EditorLoadingPage / AsyncComponent). The version-bump write stays below the guard so it is
        // not applied on a blocked attempt.
        if (isRecorderActive()) {
            setRefreshBlockedByRecording(true);
            return;
        }
        const pending = pendingRefreshVersionRef.current;
        if (pending) {
            localStorage.setItem("app_versions", JSON.stringify({ id: pending.id, version: pending.version }));
            Sentry.setTag("app_version", pending.version.toString());
        }
        setShowRefreshModal(false);
        pendingRefreshVersionRef.current = null;
        refreshApp();
    };

    const handleLaterOrDismissRefreshModal = () => {
        snoozeVersionChecksForTwoHours();
        setShowRefreshModal(false);
        pendingRefreshVersionRef.current = null;
    };

    return (
        <CacheBusterContext.Provider value={{ refreshApp }}>
            {children}
            {showRefreshModal && (
                <ConfirmModal
                    id="app-version-refresh-modal"
                    isOpen={showRefreshModal}
                    headerText="Update available"
                    contentText={
                        refreshBlockedByRecording && recorderBusy
                            ? "Still finishing your current recording — tap Refresh again in a moment to update."
                            : "A new version of HappyDoc is ready. Refresh to get the latest fixes and improvements."
                    }
                    cancelButtonText="Later"
                    confirmButtonText="Refresh"
                    confirmButtonColor="bg-max-900"
                    confirmHoverButtonColor="bg-max-800"
                    onCancel={handleLaterOrDismissRefreshModal}
                    onConfirm={handleConfirmRefresh}
                    customZIndex={10050}
                    placement="bottom-left"
                />
            )}
        </CacheBusterContext.Provider>
    );
};

export default CacheBuster;
